/**
 * WHAT HOME LEADS WITH, and it is a different answer four times a year.
 *
 * Tim's re-tooling of 23 August is a content brief, not a layout one: each phase of the season has
 * a short list of things the league is actually doing, and Home is supposed to be that list rather
 * than a fixed set of cards that go quiet out of season. Written down here rather than as
 * conditionals in the JSX for the same reason `deadlineTable` exists — the decision is the part
 * worth testing, and a table of it can be read against the brief line by line.
 *
 * THE RANKS ARE THE BRIEF'S OWN WORDS. He asked for things to be "brought to the forefront", given
 * "secondary status", and "removed" — so those are the three states, and a section that is not in
 * the returned list is removed rather than dimmed. A card nobody needs in January is not a card
 * that should sit greyed out in January; it should not be there.
 *
 *   lead       the top of the page, above everything. At most one per phase, and in three of the
 *              four it is the thing with a clock on it.
 *   primary    the body of the page, in the order given.
 *   secondary  still there, still reachable, visibly quieter.
 *
 * ONE THING NOT IN THE BRIEF, and it is deliberate: the pre-draft view does NOT drop a card when
 * its deadline passes. Tim: "this view is in place all the way until draft day, even after
 * deadlines have passed." A GM who missed the keeper deadline still needs to see that he did.
 */

import { urgencyOf, URGENCY_RANK, type Urgency } from './deadlines.js';
import type { OffseasonFocus, Phase } from './phase.js';

export type SectionId =
  | 'due'
  | 'nextAction'
  | 'yearInReview'
  | 'history'
  | 'proposals'
  | 'committee'
  | 'keeperSlate'
  | 'prefs'
  | 'picks'
  | 'order'
  | 'draftLive'
  | 'rundown'
  | 'byposition'
  | 'nextten'
  | 'targets'
  | 'farm'
  | 'trades'
  | 'faab'
  | 'wagering'
  | 'commish';

export type Rank = 'lead' | 'primary' | 'secondary';

export interface HomeSection {
  id: SectionId;
  rank: Rank;
  /** Why this is here, at this rank, in this phase. Read by the test, and by whoever edits next. */
  why: string;
}

const s = (id: SectionId, rank: Rank, why: string): HomeSection => ({ id, rank, why });

/**
 * The four views, as four lists.
 *
 * `commish` is appended by `homeSections` rather than written into each row, because it is the
 * same card in every phase — what changes is what it is counting, which is `commishTrack`'s job.
 */
const OFFSEASON_REVIEW: HomeSection[] = [
  s('yearInReview', 'lead', 'The season just ended. Looking back is the whole of what the league is doing.'),
  s('proposals', 'primary', 'Rule proposals open here and close at the far end of the offseason.'),
  s('history', 'primary', 'The records boards, which is the other half of looking back.'),
  s('due', 'secondary', 'Nothing is close. The dates are worth having, not worth leading with.'),
];

const OFFSEASON_CHECKLIST: HomeSection[] = [
  s('due', 'lead', 'Two weeks out: every upcoming deadline to the front, as the pre-season checklist.'),
  s('proposals', 'primary', 'The deadline this checklist exists for. Rule submission is the headline.'),
  s('keeperSlate', 'primary', 'Initial keeper thinking starts here — brought forward, not yet due.'),
  s('history', 'secondary', 'Dropped to secondary: it is no longer what the league is doing.'),
  /* `yearInReview` is absent on purpose. Tim: "remove year in review". */
];

const PREDRAFT: HomeSection[] = [
  s('due', 'lead', 'Three gates and a meeting, all inside a fortnight. The dates lead the page.'),
  s('committee', 'primary', 'Date, time and the way in — the commissioners set all three.'),
  s('keeperSlate', 'primary', 'The slate and its deadline, with the current state of it shown.'),
  s('prefs', 'primary', 'Slot rankings and their deadline, with the current state of them shown.'),
  /*
   * WHAT YOU ARE LEFT WITH, which is the question a slate raises and does not answer.
   *
   * The stat tiles this replaces restated three numbers a manager could already see elsewhere and
   * gave him nothing to decide with. Sixteen round cells showing which picks survive his keepers
   * and which he has traded away is the same space spent on the one thing the declaration is
   * actually a trade against.
   */
  s('picks', 'primary', 'The picks left after keepers, and what has been traded away.'),
  s('order', 'secondary', 'The lottery runs on committee night; before that this is a projection.'),
  s('history', 'secondary', 'Still reachable, no longer the point.'),
];

const DRAFT_DAY: HomeSection[] = [
  s('draftLive', 'lead', 'On the clock and how far away you are. Nothing outranks this.'),
  /*
   * THE RUNDOWN IS YOUR OWN PICKS, and it replaces "your picks so far" because the question on the
   * night is not what you have taken, it is what you have left and when. A traded round is absent
   * rather than blank: it belongs to somebody else.
   */
  s('rundown', 'primary', 'Every pick you hold, in order, keepers pre-placed.'),
  s('byposition', 'primary', 'What you have actually drafted, grouped. No invented slot counts.'),
  s('nextten', 'primary', 'How long you have, in picks rather than in minutes.'),
  s('farm', 'primary', 'The farm roster including open slots — the auction is part of the night.'),
  /*
   * A PRIVATE WATCH LIST, and draft day is the only phase it appears in. It is a list of college
   * players for an auction that happens once, on this evening; on any other day it is a card about
   * something months away.
   */
  s('targets', 'primary', 'The college players you are watching, private to you.'),
  s('trades', 'primary', 'Draft-day trades have their own window and it is running now.'),
];

const IN_SEASON: HomeSection[] = [
  s('faab', 'lead', 'Balances and transactions. This is what the league reads all autumn.'),
  s('trades', 'primary', 'The 24-hour in-season window.'),
  s('wagering', 'secondary', 'From Week 9, and easy to forget exists.'),
  s('history', 'secondary', 'Always there.'),
];

export interface SectionInput {
  phase: Phase;
  /** Only consulted in the offseason. */
  focus: OffseasonFocus;
  commish: boolean;
  /**
   * The committee has met and its day is over — see `deadlines.committeeOver`.
   *
   * The meeting card is a date, a way in, and an instruction to run the lottery before any vote.
   * All three are spent the morning after, and the phase does not turn over with them: keeper and
   * ranking deadlines keep the league in `predraft` for another week, so the card sat at the top
   * of the page saying "met" next to a dead Join button. Passed in rather than derived here
   * because the date lives in league settings and this table takes no store.
   */
  committeeMet?: boolean;
}

export function homeSections(
  { phase, focus, commish, committeeMet }: SectionInput,
): HomeSection[] {
  const table = phase === 'offseason'
    ? (focus === 'checklist' ? OFFSEASON_CHECKLIST : OFFSEASON_REVIEW)
    : phase === 'predraft' ? PREDRAFT
      : phase === 'draftday' ? DRAFT_DAY
        : IN_SEASON;

  /* A spent meeting is not demoted to secondary — there is nothing left on the card to read. */
  const base = committeeMet ? table.filter((x) => x.id !== 'committee') : table;

  if (!commish) return [...base];
  /*
   * The commissioner's card sits under the lead and above the rest — high enough that he sees the
   * count without hunting, never so high that it displaces the thing every GM came for. A manager
   * never sees it at all: conditional, never disabled-in-place, which is the rule the rest of this
   * app already follows.
   */
  const out = [...base];
  const after = out.findIndex((x) => x.rank !== 'lead');
  const card = s('commish', 'primary', 'What the league still owes, and who to chase for it.');
  if (after < 0) out.push(card); else out.splice(after, 0, card);
  return out;
}

/* ------------------------------------------------------------------------------ what a GM owes */

export interface HomeTask {
  key: string;
  title: string;
  body: string;
  /** The button. Empty where the task is done and there is nothing to press. */
  cta: string;
  to: [section: string, page: string];
  iso: string | null;
  urgency: Urgency;
  /** Whether this manager has already done it. A done task is still shown; it is just not first. */
  done: boolean;
  /** Whether the app will still accept it. */
  open: boolean;
  cites: string[];
}

export interface TaskInput {
  phase: Phase;
  focus: OffseasonFocus;
  now: number;
  proposalDeadline?: string | null;
  keeperDeadline?: string | null;
  preferenceDeadline?: string | null;
  committeeDate?: string | null;
  draftDate?: string | null;
  declarationsLocked?: boolean;
  preferencesLocked?: boolean;
  /** Has this manager written a proposal, declared a slate, ranked his slots. */
  wroteProposal: boolean;
  declared: boolean;
  ranked: boolean;
  maxKeepers: number;
  freeKeepers: number;
  /**
   * The third-place option, when it is this manager's and still unanswered (Pass 06 §3).
   *
   * A STANDING ITEM, not a prompt. Silence declines, and the reveal ends in a room full of people
   * talking — exactly when a modal gets dismissed and never thought about again. Home lists what
   * is outstanding, so this sits there until he answers and then disappears on its own.
   */
  swapOutstanding?: { title: string } | null;
}

/**
 * Everything this manager owes, most pressing first.
 *
 * ORDER IS BY URGENCY THEN DATE, NOT BY IMPORTANCE, and that is the one rule this list has: an
 * opinion about which deadline matters more is an opinion that goes stale, whereas "the one that
 * shuts first" is a fact and changes on its own as the preseason moves.
 *
 * DONE TASKS STAY. The old Home showed one card and hid it the moment you complied, which meant
 * the screen could not answer "did I already do that?" — the single most common question in the
 * week before the keeper deadline. They sort to the bottom and say so.
 */
export function homeTasks(i: TaskInput): HomeTask[] {
  const out: HomeTask[] = [];
  const open = (iso: string | null | undefined, locked?: boolean) =>
    !locked && (!iso || Date.parse(iso) > i.now);

  /*
   * WHICH TASKS EXIST IS THE PHASE'S DECISION. Ranking slots in January is not a thing a GM can
   * usefully be nagged about — the lottery has not run and the form is about a draft nobody has
   * dated — so the offseason offers exactly one task and the checklist adds the second.
   */
  const wants = {
    proposal: i.phase === 'offseason' || i.phase === 'predraft',
    keepers: (i.phase === 'offseason' && i.focus === 'checklist') || i.phase === 'predraft',
    prefs: i.phase === 'predraft',
  };

  if (wants.proposal) {
    out.push({
      key: 'proposal',
      title: 'Propose a rule change',
      body: 'Anything you want the Competition Committee to vote on this year.',
      cta: i.wroteProposal ? 'Write another' : 'Write one',
      to: ['league', 'proposals'],
      iso: i.proposalDeadline ?? null,
      urgency: urgencyOf(i.proposalDeadline, i.now),
      done: i.wroteProposal,
      open: open(i.proposalDeadline),
      cites: [],
    });
  }
  if (wants.keepers) {
    out.push({
      key: 'keepers',
      title: 'Declare your keepers',
      body: `Up to ${i.maxKeepers} players, the first ${i.freeKeepers} free of tax.`,
      cta: i.declared ? 'Review your slate' : 'Declare keepers',
      to: ['keepers', 'slate'],
      iso: i.keeperDeadline ?? null,
      urgency: urgencyOf(i.keeperDeadline, i.now),
      done: i.declared,
      open: open(i.keeperDeadline, i.declarationsLocked),
      cites: ['2023-keeper-expansion-eight'],
    });
  }
  if (wants.prefs) {
    out.push({
      key: 'prefs',
      title: 'Rank your draft slots',
      /*
       * 2020-II-1 §6, stated as the consequence rather than as the rule: a GM who submits nothing
       * takes the earliest slot still free when his turn comes. That is the sentence that gets
       * people to fill the form, and it is also the answer to "what happens if I don't".
       */
      body: 'Submit nothing and you take the earliest slot left when your turn comes.',
      cta: i.ranked ? 'Change your ranking' : 'Rank slots',
      to: ['draft', 'order'],
      iso: i.preferenceDeadline ?? null,
      urgency: urgencyOf(i.preferenceDeadline, i.now),
      done: i.ranked,
      open: open(i.preferenceDeadline, i.preferencesLocked),
      cites: ['2020-II-1'],
    });
  }

  /*
   * ALWAYS OFFERED WHERE IT EXISTS, whatever the phase. Every other task here belongs to a phase
   * because it belongs to a deadline the league sets; this one belongs to a draw that has already
   * happened, and it expires whether or not anybody is looking at the right screen.
   */
  if (i.swapOutstanding) {
    out.push({
      key: 'third-place-swap',
      title: i.swapOutstanding.title,
      body: 'You may take fourth place’s lottery result instead of your own. Binding once '
        + 'declared, and it lapses if you say nothing.',
      cta: 'Take or decline',
      to: ['draft', 'order'],
      iso: i.preferenceDeadline ?? null,
      urgency: urgencyOf(i.preferenceDeadline, i.now),
      done: false,
      open: open(i.preferenceDeadline, i.preferencesLocked),
      cites: ['2018-II-5'],
    });
  }

  return out.sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    const u = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    if (u !== 0) return u;
    return (a.iso ?? '9999').localeCompare(b.iso ?? '9999');
  });
}

/* --------------------------------------------------------------------- what a commissioner owes */

export interface TrackRow {
  key: string;
  label: string;
  /** "6 of 10", or a bare count where there is no denominator (proposals have no quota). */
  count: string;
  /** How many are still missing, for the "N to chase" summary. Zero where nothing is owed. */
  outstanding: number;
  note: string;
  /** Somebody needs a nudge. Drives the colour and the summary count. */
  chase: boolean;
  to: [section: string, page: string];
}

export interface TrackInput {
  phase: Phase;
  focus: OffseasonFocus;
  teams: number;
  /** How many proposals actually reached the committee — see `domain/proposals.ts`. */
  proposals: number;
  /** How many of those the committee has not answered yet. */
  proposalsUndecided?: number;
  declared: number;
  /** How many rankings the resolve will have a list for — `rankingCensus().willResolve`. */
  ranked: number;
  /**
   * The census's own sub-line, when there is more to say than "N to chase".
   *
   * A ranking that was saved and never sent is read by the resolve and shown as missing by every
   * counter, so a commissioner chasing this row was chasing men whose lists he already had. See
   * `domain/rankingCensus.ts`.
   */
  rankedNote?: string;
  lotteryRun: boolean;
  orderPublished: boolean;
  /** Draft day only: the on-the-clock line, already worded by `pickWatch`. */
  onClock?: { line: string; chase: boolean } | null;
  picksMade?: number;
  picksTotal?: number;
}

/**
 * The commissioner's tracking card, which is a different card in every phase because the thing he
 * is chasing changes completely.
 *
 * NO ROW WITHOUT A NUMBER. Every row here is a count he can act on — "0 of 10 have ranked" tells
 * him to post in Discord tonight. Rows that would only restate a date belong in the deadline rail
 * where every GM sees them too, and duplicating them here was how the old commissioner card grew
 * into a second copy of the page.
 */
export function commishTrack(i: TrackInput): TrackRow[] {
  const of = (done: number) => `${done} of ${i.teams}`;
  const short = (done: number) => Math.max(0, i.teams - done);

  if (i.phase === 'offseason') {
    /*
     * "commish view: keep track of how many have come in" — and proposals have no denominator.
     * Ten GMs may write none, one, or nine between them, so a count of ten would invent a quota
     * the rulebook has never had.
     */
    const rows: TrackRow[] = [{
      key: 'proposals',
      label: 'Rule proposals in',
      count: `${i.proposals}`,
      outstanding: 0,
      note: i.proposals === 0 ? 'nobody has written one yet' : 'no quota — this is how many exist',
      chase: i.focus === 'checklist' && i.proposals === 0,
      to: ['league', 'proposals'],
    }];
    return rows;
  }

  if (i.phase === 'predraft') {
    return [
      {
        key: 'proposals',
        label: 'On the docket',
        count: `${i.proposals}`,
        outstanding: i.proposalsUndecided ?? 0,
        /*
         * WHAT IS LEFT, not what the row is for. "What the committee votes on" is a definition,
         * and it went on saying it after the committee had voted on all of them — so a card whose
         * whole job is "what is still outstanding" had one row that could never be finished.
         */
        note: i.proposals === 0 ? 'nothing was put to the committee'
          : (i.proposalsUndecided ?? 0) === 0 ? 'all decided'
            : `${i.proposalsUndecided} still to vote on`,
        chase: (i.proposalsUndecided ?? 0) > 0,
        to: ['league', 'proposals'],
      },
      {
        key: 'keepers',
        label: 'Keeper slates in',
        count: of(i.declared),
        outstanding: short(i.declared),
        note: short(i.declared) === 0 ? 'all in' : `${short(i.declared)} to chase`,
        chase: short(i.declared) > 0,
        to: ['keepers', 'slate'],
      },
      {
        key: 'prefs',
        label: 'Slot rankings in',
        count: of(i.ranked),
        outstanding: short(i.ranked),
        note: i.rankedNote ?? (short(i.ranked) === 0 ? 'all in' : `${short(i.ranked)} to chase`),
        chase: short(i.ranked) > 0,
        to: ['draft', 'order'],
      },
      {
        key: 'lottery',
        label: 'Lottery',
        count: i.orderPublished ? 'published' : i.lotteryRun ? 'drawn' : 'not run',
        outstanding: i.orderPublished ? 0 : 1,
        /*
         * THE NOTE FOLLOWS THE STATE. It said "run it on committee night, before any vote" in all
         * three, so a commissioner who had already run it was still being told to run it. The
         * scheduling line is the right thing to say to a man who has not drawn yet and the wrong
         * thing to say to one who has — 2020-II-1 has the draw happening on committee night before
         * any vote is cast, which is the fact he gets wrong BEFOREHAND.
         */
        note: i.orderPublished ? 'the league has the order'
          : i.lotteryRun ? 'drawn — the order publishes when rankings close'
            : 'run it on committee night, before any vote',
        chase: !i.orderPublished,
        to: ['draft', 'order'],
      },
    ];
  }

  if (i.phase === 'draftday') {
    const rows: TrackRow[] = [];
    if (i.onClock) {
      rows.push({
        key: 'onclock',
        label: 'On the clock',
        count: i.onClock.line,
        outstanding: i.onClock.chase ? 1 : 0,
        note: i.onClock.chase ? 'past the announced clock' : 'inside the clock',
        chase: i.onClock.chase,
        to: ['draft', 'board'],
      });
    }
    if (i.picksTotal) {
      rows.push({
        key: 'picks',
        label: 'Picks made',
        count: `${i.picksMade ?? 0} of ${i.picksTotal}`,
        outstanding: Math.max(0, i.picksTotal - (i.picksMade ?? 0)),
        note: 'the board',
        chase: false,
        to: ['draft', 'board'],
      });
    }
    return rows;
  }

  return [];
}

/** How many rows want a commissioner to do something. The number on the card's header. */
export const chasing = (rows: readonly TrackRow[]): number => rows.filter((r) => r.chase).length;
